import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, ProjectStatusBadge, budgetColor } from "@/mockups/ui";
import { toast } from "sonner";
import { Plus, Search, CheckSquare, Clock } from "lucide-react";
import { fmtHM } from "@/lib/utils";

const EMPTY = { name: "", company_id: "none", manager_id: "none", description: "", status: "active", budget_hours: 0, due_date: "", external_payments_enabled: false };
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");

function ProjectDialog({ open, onOpenChange, companies, team, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description, status: form.status,
        budget_hours: Number(form.budget_hours) || 0,
        company_id: form.company_id === "none" ? null : form.company_id,
        manager_id: form.manager_id === "none" ? null : form.manager_id,
        due_date: form.due_date || null,
        external_payments_enabled: !!form.external_payments_enabled,
      };
      await api.post("/projects", payload);
      toast.success("Proyecto creado");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nuevo proyecto</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 space-y-1.5"><Label>Nombre *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="project-name-input" /></div>
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
              <SelectTrigger data-testid="project-company-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin cliente</SelectItem>
                {(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Responsable</Label>
            <Select value={form.manager_id} onValueChange={(v) => set("manager_id", v)}>
              <SelectTrigger data-testid="project-manager-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {(team || []).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Estado</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="on_hold">En pausa</SelectItem>
                <SelectItem value="done">Finalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Presupuesto (horas)</Label><Input type="number" value={form.budget_hours} onChange={(e) => set("budget_hours", e.target.value)} data-testid="project-budget-input" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Fecha límite</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></div>
          <div className="col-span-2 space-y-1.5"><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="resize-none" /></div>
          <label className="col-span-2 flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-muted/40" data-testid="project-extpay-option">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={!!form.external_payments_enabled} onChange={(e) => set("external_payments_enabled", e.target.checked)} data-testid="project-extpay-checkbox" />
            <span>
              <span className="text-sm font-medium">Añadir Pagos externos</span>
              <span className="block text-xs text-muted-foreground mt-0.5">Activa la pestaña de intermediación de pagos a terceros (+3,5%) en este proyecto. Podrás activarla o quitarla más adelante.</span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="project-save-btn">{saving ? "Guardando…" : "Crear proyecto"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Proyectos() {
  const { can } = useAuth();
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const { data: projects, loading, refetch } = useFetch("/projects", status !== "all" ? { status } : {});
  const { data: companies } = useFetch("/companies");
  const { data: team } = useFetch("/team");
  const [dialogOpen, setDialogOpen] = useState(false);

  const term = q.trim().toLowerCase();
  const projNum = (p) => {
    const n = parseInt((p.reference || "").replace(/\D/g, ""), 10);
    return Number.isNaN(n) ? -Infinity : n;
  };
  const list = (projects || [])
    .filter((p) => !term
      || (p.name || "").toLowerCase().includes(term)
      || (p.company_name || "").toLowerCase().includes(term)
      || (p.reference || "").toLowerCase().includes(term))
    .sort((a, b) => projNum(b) - projNum(a)); // por número de proyecto, mayor a menor

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Proyectos" subtitle="Planificación, progreso y rentabilidad de cada proyecto">
        {can("projects", "create") && <Button size="sm" className="gap-1.5" onClick={() => setDialogOpen(true)} data-testid="new-project-btn"><Plus className="h-4 w-4" /> Nuevo proyecto</Button>}
      </PageHeader>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList data-testid="project-filter">
            <TabsTrigger value="active">Activos</TabsTrigger>
            <TabsTrigger value="on_hold">En pausa</TabsTrigger>
            <TabsTrigger value="done">Finalizados</TabsTrigger>
            <TabsTrigger value="all">Todos</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por nombre, cliente o ref…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="project-search" />
        </div>
      </div>

      {loading ? <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        : list.length === 0 ? <Card className="border-border shadow-none py-16 text-center text-sm text-muted-foreground">{term ? "Ningún proyecto coincide con la búsqueda." : "No hay proyectos en este estado."}</Card>
        : (
        <Card className="border-border shadow-none overflow-hidden divide-y divide-border">
          {list.map((p) => {
            const pct = p.budget_hours > 0 ? Math.round((p.hours_spent / p.budget_hours) * 100) : 0;
            return (
              <Link key={p.id} to={`/proyecto/${p.id}`} data-testid={`project-row-${p.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors duration-150">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-semibold text-sm truncate" title={p.name}>{p.name}</span>
                    {p.reference && <span className="text-[11px] font-mono text-muted-foreground bg-muted rounded px-1.5 py-0.5 shrink-0">{p.reference}</span>}
                  </div>
                  <span className="text-xs text-muted-foreground truncate block mt-0.5">{p.company_name || "Sin cliente"}</span>
                </div>

                <div className="hidden lg:flex items-center gap-2 shrink-0 w-40">
                  <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-muted">{initials(p.manager_name) || "—"}</AvatarFallback></Avatar>
                  <span className="text-xs text-muted-foreground truncate">{p.manager_name || "Sin responsable"}</span>
                </div>

                <div className="hidden md:flex items-center gap-1 shrink-0 w-20 text-xs text-muted-foreground tabular-nums" title="Tareas hechas / totales">
                  <CheckSquare className="h-3.5 w-3.5" /> {p.tasks_done}/{p.tasks_total}
                </div>

                <div className="hidden sm:block shrink-0 w-28" title="Presupuesto de horas">
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="text-muted-foreground inline-flex items-center gap-1"><Clock className="h-3 w-3" /></span>
                    <span className={pct >= 100 ? "text-destructive font-semibold tabular-nums" : "tabular-nums text-muted-foreground"}>{fmtHM(p.hours_spent)} / {p.budget_hours ? fmtHM(p.budget_hours) : "–"}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${budgetColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
                </div>

                <span className="hidden xl:block text-xs text-muted-foreground w-24 text-right shrink-0">{p.due_date ? `Vence ${new Date(p.due_date).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}` : ""}</span>

                <div className="shrink-0"><ProjectStatusBadge status={p.status} /></div>
              </Link>
            );
          })}
        </Card>
      )}

      {dialogOpen && <ProjectDialog open={dialogOpen} onOpenChange={setDialogOpen} companies={companies} team={team} onSaved={refetch} />}
    </div>
  );
}
